import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useClothesStore, ClothesItem } from '../store/clothesStore';
import { useApiCall } from '../hooks/useApiCall';
import { api } from '../api/api';
import Input from './Input';
import Button from './Button';
import CustomDropdown from './CustomDropdown';
import Loader from './Loader';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 18px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #FFFFFF;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #609F94;
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1 / -1;
  margin-top: 4px;
`;

const CompactInput = styled(Input)`
  input {
    padding: 6px 10px !important;
    font-size: 13px !important;
    min-height: unset !important;
    height: 32px !important;
  }
`;

const StyledCheckbox = styled.input`
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #609F94;
  border-radius: 3px;
  background-color: transparent;
  cursor: pointer;
  position: relative;

  &:checked {
    background-color: #609F94;
    border-color: #609F94;
  }

  &:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 10px;
    font-weight: bold;
  }
`;

const CheckboxLabel = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #FFFFFF;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  max-height: 70vh;
  padding-right: 8px;
  margin-right: -8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #1A2E2E;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #609F94;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #7BC4B8;
  }
`;

import type { IMetroStation } from "../api/api";

interface EditClothesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clothesItem: ClothesItem;
}

interface FormData {
  size: string;
  brand: string;
  price: string;
  article: string;
  condition: string;
  city: string;
  place: string;
  fitting: boolean;
}

const EditClothesModal: React.FC<EditClothesModalProps> = ({ isOpen, onClose, clothesItem }) => {
  const { updateClothes } = useClothesStore();
  const { executeApiCall, loading } = useApiCall();

  const [formData, setFormData] = useState<FormData>({
    size: '',
    brand: '',
    price: '',
    article: '',
    condition: '',
    city: '',
    place: '',
    fitting: false,
  });

  const [brands, setBrands] = useState<{ value: string; label: string }[]>([]);
  const [conditions, setConditions] = useState<{ value: string; label: string }[]>([]);
  const [sizes, setSizes] = useState<{ value: string; label: string }[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [metroStations, setMetroStations] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && clothesItem) {
      setFormData({
        size: clothesItem.size,
        brand: clothesItem.brand,
        price: clothesItem.price.replace(' ₽', ''),
        article: clothesItem.article,
        condition: clothesItem.condition,
        city: clothesItem.city,
        place: clothesItem.place,
        fitting: clothesItem.fitting,
      });
      loadDropdownData();
    }
  }, [isOpen, clothesItem]);

  const loadDropdownData = async () => {
    await Promise.all([
      loadBrands(),
      loadConditions(),
      loadSizes(),
      loadCities(),
      loadMetroStations()
    ]);
  };

  const loadBrands = async () => {
    try {
      const response = await api.getBrands();
      if (response && response.brands) {
        setBrands(response.brands.map((brand: any) => ({
          value: brand.name,
          label: brand.name
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки брендов:', error);
    }
  };

  const loadConditions = async () => {
    try {
      const response = await api.getConditions();
      if (response && response.conditions) {
        setConditions(response.conditions.map((condition: any) => ({
          value: condition.name,
          label: condition.name
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки состояний:', error);
    }
  };

  const loadSizes = async () => {
    try {
      const response = await api.getSizes();
      if (response && response.sizes) {
        setSizes(response.sizes.map((size: any) => ({
          value: size.name,
          label: size.name
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки размеров:', error);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.getAvailableCities();
      if (response && response.cities) {
        setCities(response.cities);
      }
    } catch (error) {
      console.error('Ошибка загрузки городов:', error);
    }
  };

  const loadMetroStations = async () => {
    try {
      const response = await api.getMetroStations();
      if (response && response.stations) {
        setMetroStations(response.stations);
      }
    } catch (error) {
      console.error('Ошибка загрузки станций метро:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDropdownChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      size: formData.size,
      brand: formData.brand,
      price: parseFloat(formData.price),
      article: formData.article,
      condition: formData.condition,
      city: formData.city,
      place: formData.place,
      fitting: formData.fitting,
    };

    await executeApiCall(
      () => updateClothes(clothesItem.id, data),
      {
        successMessage: 'Одежда успешно обновлена',
        errorMessage: 'Ошибка при обновлении одежды'
      }
    );

    onClose();
  };

  const resetForm = () => {
    setFormData({
      size: '',
      brand: '',
      price: '',
      article: '',
      condition: '',
      city: '',
      place: '',
      fitting: false,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Редактировать одежду</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ScrollableContent>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormField>
                <Label>Артикул</Label>
                <CompactInput
                  name="article"
                  value={formData.article}
                  onChange={handleInputChange}
                  placeholder="Введите артикул"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Бренд"
                  options={brands}
                  value={formData.brand}
                  onChange={(value: string) => handleDropdownChange('brand', value)}
                  placeholder="Выберите бренд"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Размер"
                  options={sizes}
                  value={formData.size}
                  onChange={(value: string) => handleDropdownChange('size', value)}
                  placeholder="Выберите размер"
                />
              </FormField>

              <FormField>
                <Label>Цена</Label>
                <CompactInput
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="Введите цену"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Состояние"
                  options={conditions}
                  value={formData.condition}
                  onChange={(value: string) => handleDropdownChange('condition', value)}
                  placeholder="Выберите состояние"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Город"
                  options={cities.map((city) => ({
                    value: city,
                    label: city,
                  }))}
                  value={formData.city}
                  onChange={(value: string) => handleDropdownChange('city', value)}
                  placeholder="Выберите город"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Место встречи (станция метро)"
                  options={metroStations.map((station) => ({
                    value: station,
                    label: station,
                  }))}
                  value={formData.place}
                  onChange={(value: string) => handleDropdownChange('place', value)}
                  placeholder="Выберите станцию метро"
                />
              </FormField>

              <CheckboxField>
                <StyledCheckbox
                  type="checkbox"
                  id="fitting"
                  name="fitting"
                  checked={formData.fitting}
                  onChange={handleInputChange}
                />
                <CheckboxLabel htmlFor="fitting">Примерка</CheckboxLabel>
              </CheckboxField>
            </FormGrid>

            <ButtonContainer>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Сохранить'}
              </Button>
            </ButtonContainer>
          </form>
        </ScrollableContent>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditClothesModal;
